import {Alert, Box, Button, Chip, Grid, Tooltip} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import React, {useMemo, useState} from 'react';
import {useQuery} from '@apollo/client';
import {useNavigate} from 'react-router-dom';
import {GridColDef} from '@mui/x-data-grid-pro';
import {SearchResults} from '../Common/SearchResults';
import {BankAccountValidationDisplay} from '../Common/BankAccountValidationDisplay';
import {useAccountContext} from '../../contexts/AccountContext';
import {useDjangoContext} from '../../contexts/useAuthContext';
import {Panel} from '../Panel/Panel';
import Loading from '../Loading/Loading';
import {themeColors} from '../../theme/Theme';
import validateUUID from '../../utils/validateUUID';
import ACCOUNT_TAB from '../../constants/AccountTab';
import {GET_BANK_ACCOUNTS} from './accountOverviewQueries';
import {
  BankAccounts,
  BankAccountsVariables,
  BankAccounts_bankAccounts,
} from '../../schema-types/BankAccounts';
import {hasPermission} from '../../contexts/FeatureBoundary';
import {GlobalPermissionEnum} from '../../schema-types/globalTypes';
import {useContactDefaultBankAccounts} from '../../features/defaultBankAccounts/useContactDefaultBankAccounts';
import {
  holderName,
  SetDefaultBankAccountHolder,
  SetDefaultBankAccountModal,
} from '../../features/defaultBankAccounts/SetDefaultBankAccountModal';

const VALIDATE_FIRST = 'Bank account must be validated before it can be a default';

type DefaultTarget = {
  bankAccount: BankAccounts_bankAccounts;
  holders: [SetDefaultBankAccountHolder, ...SetDefaultBankAccountHolder[]];
};

export const AccountBankAccounts: React.FC = () => {
  const {accountId} = useAccountContext();
  const {djangoUser} = useDjangoContext();
  const navigate = useNavigate();
  const defaults = useContactDefaultBankAccounts(accountId);
  const [target, setTarget] = useState<DefaultTarget | null>(null);
  const {data, loading} = useQuery<BankAccounts, BankAccountsVariables>(
    GET_BANK_ACCOUNTS,
    {
      variables: {accountId: validateUUID(accountId)!},
      skip: !accountId,
      nextFetchPolicy: 'no-cache',
      fetchPolicy: 'no-cache',
    }
  );
  const bankAccounts = data?.bankAccounts || [];

  const isAdministrator = hasPermission(djangoUser, [
    GlobalPermissionEnum.can_access_admin_features,
  ]);

  const columns: GridColDef[] = useMemo(() => {
    const adminOnlyColumns: GridColDef[] = isAdministrator
      ? [
          {
            field: 'curoId',
            sortable: false,
            headerName: 'Curo ID (admin visible only)',
            width: 300,
          },
        ]
      : [];

    const defaultColumns: GridColDef[] = defaults.isEnabled
      ? [
          {
            field: 'defaultFor',
            sortable: false,
            headerName: 'Contact default account',
            minWidth: 240,
            flex: 1,
            renderCell: (params) => {
              const bankAccount = params.row as BankAccounts_bankAccounts;
              const names = (defaults.holdersByDefault[bankAccount.id] ?? [])
                .map(holderName)
                .join(', ');
              // A contact's own account serves only that contact; a joint one serves any holder.
              const [first, ...rest] = defaults.holdersWithoutDefault.filter(
                (holder) => !bankAccount.contact || holder.id === bankAccount.contact.id
              );
              return (
                <Box display="flex" alignItems="center" gap={1} height="100%">
                  {names ? (
                    <Chip
                      // The Contact column already names an own account's holder; a joint one says "Joint".
                      label={bankAccount.contact ? 'Default' : `Default for ${names}`}
                      size="small"
                      color="success"
                      icon={<CheckIcon fontSize="small" />}
                    />
                  ) : null}
                  {first && bankAccount.isValidated ? (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setTarget({bankAccount, holders: [first, ...rest]})}
                    >
                      Set as default
                    </Button>
                  ) : null}
                  {first && !bankAccount.isValidated ? (
                    <Tooltip title={VALIDATE_FIRST}>
                      <span>
                        <Button variant="outlined" size="small" disabled>
                          Set as default
                        </Button>
                      </span>
                    </Tooltip>
                  ) : null}
                </Box>
              );
            },
          },
        ]
      : [];

    return [
      {
        field: 'accountName',
        sortable: false,
        headerName: 'Account Name',
        flex: 2,
      },
      {
        field: 'accountNumber',
        sortable: false,
        headerName: 'Account Number',
        width: 140,
      },
      {
        field: 'sortCode',
        sortable: false,
        headerName: 'Sort Code',
        width: 100,
      },
      {
        field: 'provider',
        sortable: false,
        headerName: 'Provider',
        valueGetter: (value, row) => {
          const bankAccount = row as BankAccounts_bankAccounts;
          return bankAccount.provider?.formattedName;
        },
        flex: 2,
      },
      {
        field: 'contact',
        sortable: false,
        headerName: 'Contact',
        valueGetter: (value, row) => {
          const bankAccount = row as BankAccounts_bankAccounts;
          return bankAccount.contact?.simplifiedName || 'Joint';
        },
        flex: 1,
      },
      // {
      //   field: 'status',
      //   sortable: false,
      //   headerName: 'Status',
      //   flex: 1,
      // },
      {
        field: 'isValidated',
        sortable: false,
        headerName: 'Validation',
        width: 150,
        renderCell: (params) => {
          const bankAccount = params.row as BankAccounts_bankAccounts;
          return (
            <Box display="flex" alignItems="center" height="100%">
              <BankAccountValidationDisplay isValidated={bankAccount.isValidated} />
            </Box>
          );
        },
      },
      ...defaultColumns,
      ...adminOnlyColumns,
    ];
  }, [
    isAdministrator,
    defaults.isEnabled,
    defaults.holdersByDefault,
    defaults.holdersWithoutDefault,
  ]);

  if (loading) {
    return (
      <Grid item xs={12}>
        <Panel color={themeColors.lightGreen} title="Bank accounts">
          <Loading entityName="bank accounts" />
        </Panel>
      </Grid>
    );
  }

  return (
    <Grid item xs={12}>
      {defaults.holdersWithoutDefault.map((holder) => (
        <Alert key={holder.id} severity="warning" sx={{mb: 1}}>
          {holderName(holder)} has no default bank account. Set one before submitting
          instructions.
        </Alert>
      ))}
      <SearchResults
        dataGridId="AccountBankAccounts"
        alternateRowBg
        placeholder="No bank accounts"
        title={`Bank accounts (${bankAccounts.length})`}
        showButton
        buttonTitle="New bank account"
        buttonOnClick={() =>
          navigate({pathname: `/account/${accountId}/${ACCOUNT_TAB.NEW_BANK_ACCOUNT}`})
        }
        autoHeight={false}
        search=""
        loading={loading}
        hideFooter
        sx={{
          '& *': {
            textAlign: 'left',
            '-ms-overflow-style': 'none',
          },
          '& ::-webkit-scrollbar': {
            width: '0 !important',
          },
        }}
        data={bankAccounts}
        columns={columns}
        minHeight={false}
        rowHeight={40}
        onRowDoubleClick={() => {}}
      />
      {target ? (
        <SetDefaultBankAccountModal
          key={target.bankAccount.id}
          open
          onClose={() => setTarget(null)}
          onSaved={defaults.refetch}
          accountId={accountId!}
          bankAccount={target.bankAccount}
          holders={target.holders}
        />
      ) : null}
    </Grid>
  );
};
